import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { useAuth } from "../App";
import {
  getProgressEligibleStudents, getSchoolSettings, getTraditionalGrades,
  saveTraditionalGrades, updateGradingSubjects,
} from "../lib/api";

const LETTERS = ["A", "B", "C", "D", "F"];

function letterFromPct(pct) {
  if (pct === "" || pct === null || pct === undefined) return null;
  const p = Number(pct);
  if (Number.isNaN(p)) return null;
  if (p >= 90) return "A";
  if (p >= 80) return "B";
  if (p >= 70) return "C";
  if (p >= 60) return "D";
  return "F";
}

const letterColor = (l) =>
  l === "A" || l === "B" ? "bg-green-100 text-green-800"
  : l === "C" ? "bg-amber-100 text-amber-800"
  : l === "D" || l === "F" ? "bg-red-100 text-red-800"
  : "bg-gray-100 text-gray-500";

export default function TraditionalGradesGrid({ semester, school_year }) {
  const { isAdmin } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  // grid[studentId][subject] = { percentage: string, letter: string|null, override: bool }
  const [grid, setGrid] = useState({});
  const [dirty, setDirty] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newSubject, setNewSubject] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, studentsRes, gradesRes] = await Promise.all([
        getSchoolSettings(),
        getProgressEligibleStudents(semester, school_year),
        getTraditionalGrades(semester, school_year),
      ]);
      setSubjects(settingsRes.data.grading_subjects || []);
      const roster = (studentsRes.data || [])
        .sort((a, b) => (a.last_name || "").localeCompare(b.last_name || ""));
      setStudents(roster);
      const loaded = gradesRes.data.grades || {};
      const g = {};
      for (const s of roster) {
        g[s.id] = {};
        const sg = loaded[s.id] || {};
        for (const subj of (settingsRes.data.grading_subjects || [])) {
          const cell = sg[subj] || {};
          g[s.id][subj] = {
            percentage: cell.percentage != null ? String(cell.percentage) : "",
            letter: cell.letter || null,
          };
        }
      }
      setGrid(g);
      setDirty(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load grades");
    } finally {
      setLoading(false);
    }
  }, [semester, school_year]);

  useEffect(() => { load(); }, [load]);

  const markDirty = (studentId, subject) =>
    setDirty((d) => new Set(d).add(`${studentId}|${subject}`));

  const setPct = (studentId, subject, value) => {
    setGrid((prev) => {
      const cell = prev[studentId]?.[subject] || {};
      // Setting a percentage recomputes the letter (clears manual override).
      return {
        ...prev,
        [studentId]: { ...prev[studentId], [subject]: { ...cell, percentage: value, letter: letterFromPct(value) } },
      };
    });
    markDirty(studentId, subject);
  };

  const setLetter = (studentId, subject, value) => {
    setGrid((prev) => {
      const cell = prev[studentId]?.[subject] || {};
      return {
        ...prev,
        [studentId]: { ...prev[studentId], [subject]: { ...cell, letter: value || null } },
      };
    });
    markDirty(studentId, subject);
  };

  const dirtyCount = dirty.size;

  const handleSave = async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    try {
      const entries = [];
      for (const key of dirty) {
        const [studentId, subject] = key.split("|");
        const cell = grid[studentId]?.[subject] || {};
        entries.push({
          student_id: studentId,
          subject,
          percentage: cell.percentage === "" || cell.percentage == null ? null : Number(cell.percentage),
          letter: cell.letter || null,
        });
      }
      const res = await saveTraditionalGrades({ semester, school_year, entries });
      toast.success(`Saved ${res.data.saved} grade${res.data.saved === 1 ? "" : "s"}`);
      setDirty(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addSubject = async () => {
    const name = newSubject.trim();
    if (!name || subjects.includes(name)) { setNewSubject(""); return; }
    try {
      const res = await updateGradingSubjects([...subjects, name]);
      setSubjects(res.data.grading_subjects);
      setNewSubject("");
      toast.success(`Added ${name}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add subject");
    }
  };

  const removeSubject = async (name) => {
    if (subjects.length <= 1) { toast.error("At least one subject is required"); return; }
    try {
      const res = await updateGradingSubjects(subjects.filter((s) => s !== name));
      setSubjects(res.data.grading_subjects);
      toast.success(`Removed ${name}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove subject");
    }
  };

  const totalCols = useMemo(() => subjects.length, [subjects]);

  return (
    <div className="space-y-4" data-testid="traditional-grades-grid">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-base">Grades — {semester}</CardTitle>
              <CardDescription>
                Enter a percentage (0–100); the letter grade fills in automatically (A ≥ 90, B ≥ 80, C ≥ 70, D ≥ 60, F &lt; 60).
                You can override the letter with the dropdown.
              </CardDescription>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving || dirtyCount === 0}
              className="bg-[#00305E] hover:bg-[#00305E]/90"
              data-testid="grades-save-btn"
            >
              {saving ? "Saving…" : dirtyCount > 0 ? `Save ${dirtyCount} change${dirtyCount === 1 ? "" : "s"}` : "Saved"}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {isAdmin() && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground mr-1">Subjects:</span>
              {subjects.map((s) => (
                <Badge key={s} variant="outline" className="gap-1" data-testid={`grade-subject-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                  {s}
                  <button onClick={() => removeSubject(s)} className="ml-1 text-muted-foreground hover:text-red-600" data-testid={`remove-subject-${s.replace(/\s+/g, "-").toLowerCase()}`}>×</button>
                </Badge>
              ))}
              <Input
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addSubject()}
                placeholder="Add subject"
                className="h-7 w-40 text-xs"
                data-testid="add-subject-input"
              />
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addSubject} data-testid="add-subject-btn">Add</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : students.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No students yet. Add students to record grades.</div>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#00305E] text-white">
                  <th className="sticky left-0 z-10 bg-[#00305E] text-left px-3 py-2 min-w-[160px]">Student</th>
                  {subjects.map((subj) => (
                    <th key={subj} className="px-3 py-2 text-center min-w-[130px]">{subj}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map((s, i) => (
                  <tr key={s.id} className={i % 2 ? "bg-muted/30" : ""} data-testid={`grade-row-${s.id}`}>
                    <td className="sticky left-0 z-10 px-3 py-2 font-medium whitespace-nowrap" style={{ backgroundColor: i % 2 ? "#f5f5f7" : "white" }}>
                      {s.first_name} {s.last_name}
                    </td>
                    {subjects.map((subj) => {
                      const cell = grid[s.id]?.[subj] || { percentage: "", letter: null };
                      return (
                        <td key={subj} className="px-2 py-1.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={cell.percentage}
                              onChange={(e) => setPct(s.id, subj, e.target.value)}
                              className="h-8 w-16 text-center px-1"
                              placeholder="%"
                              data-testid={`grade-pct-${s.id}-${subj.replace(/\s+/g, "-").toLowerCase()}`}
                            />
                            <select
                              value={cell.letter || ""}
                              onChange={(e) => setLetter(s.id, subj, e.target.value)}
                              className={`h-8 rounded-md border text-xs font-bold px-1 ${letterColor(cell.letter)}`}
                              data-testid={`grade-letter-${s.id}-${subj.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                              <option value="">—</option>
                              {LETTERS.map((l) => <option key={l} value={l}>{l}</option>)}
                            </select>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      {totalCols === 0 && (
        <p className="text-xs text-muted-foreground">No subjects configured. {isAdmin() ? "Add one above." : "Ask an admin to add subjects."}</p>
      )}
    </div>
  );
}
